import React, { useState, useEffect } from "react";
import { Input, Select, Textarea, FormField, Button, SectionTitle, StatusBadge, FileInput } from "../../../SharedInputs.jsx";
import { DollarSign, BarChart2, Calendar, GripVertical, Mail, CheckCircle, Receipt, Section  } from "lucide-react";


const calculateCommission = (amount, rate) =>
  Number(((parseFloat(amount || 0) * parseFloat(rate || 0)) / 100).toFixed(2));


export const InstallmentCard = ({
  idx,
  installment,
  commissionRate,
  onChange,
  onRemove,
  onGenerateInvoice,
  onGenerateReceipt,
  label,
  alwaysExpanded = false,
  highlight = false,
  customInstallments,
}) => {
  const [expanded, setExpanded] = useState(alwaysExpanded);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [generatingReceipt, setGeneratingReceipt] = useState(false);
  
  // Follow-up handlers
  const handleFollowUpChange = (fIdx, field, value) => {
    const updatedFollowUps = [...(installment.followUps || [])];
    updatedFollowUps[fIdx] = { ...updatedFollowUps[fIdx], [field]: value };
    onChange(idx, "followUps", updatedFollowUps);
  };
  const isInvoiceSentWithFine =
    installment.invoiceSent &&
    (
      Number(installment.fineAmount || 0) > 0 ||
      installment.fineSelected === true ||
      installment.fineMode === "standard"
    );

  const invoiceSentColor = isInvoiceSentWithFine ? "#9f3a38" : "#10b981";

  const invoiceSentTitle = isInvoiceSentWithFine
    ? `Invoice sent with fine${
        Number(installment.fineAmount || 0) > 0
          ? ` ($${Number(installment.fineAmount || 0).toFixed(2)})`
          : ""
      }`
    : "Invoice Sent";

  const addFollowUp = () => {
    const updatedFollowUps = [
      ...(installment.followUps || []),
      { notes: "", date: "", status: "" },
    ];
    onChange(idx, "followUps", updatedFollowUps);
  };
  const handleGenerateInvoice = async () => {
    if (!onGenerateInvoice) return;
    
    setGeneratingInvoice(true);
    try {
      await onGenerateInvoice(idx);
    } catch (error) {
      console.error('Failed to generate invoice:', error);
    } finally {
      setGeneratingInvoice(false);
    }
  };
  const handleGenerateReceipt = async () => {
    if (!onGenerateReceipt) return;

    setGeneratingReceipt(true);
    try {
      await onGenerateReceipt(idx);
    } catch (error) {
      console.error("Failed to generate receipt:", error);
    } finally {
      setGeneratingReceipt(false);
    }
  };

  const removeFollowUp = (fIdx) => {
    const updatedFollowUps = [...(installment.followUps || [])];
    updatedFollowUps.splice(fIdx, 1);
    onChange(idx, "followUps", updatedFollowUps);
  };

  return (
    <div
      style={{
        border: highlight ? "2px solid #ef4444" : "1px solid #e5e7eb",
        borderRadius: 8,
        marginBottom: 12,
        overflow: "hidden",
        background: highlight ? "#fef2f2" : "white",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: 12,
          cursor: "pointer",
          background: "#f9fafb",
          alignItems: "center",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <GripVertical style={{ cursor: "grab", opacity: 0.5 }} />
        <div>{label || `Instalment #${idx + 1}`}</div>
        <div>Instalment: ${installment.depositAmount}</div>
        <div>Due: {installment.dueDate || "-"}</div>
        <div>Commission: ${calculateCommission(installment.amountPaid, commissionRate)}</div>
        <div>
          Status: <StatusBadge status={installment.status || "pending"} />
        </div>
        {installment.invoiceSent && (
          <CheckCircle
            style={{
              color: invoiceSentColor,
              width: 20,
              height: 20,
            }}
            title={invoiceSentTitle}
          />
        )}
        <div>{expanded ? "▲" : "▼"}</div>
      </div>

      {/* Expanded Body */}
      {expanded && (
        <div style={{ padding: 12, background: "#fff", width: "100%" }}>
          {/* Fee Details */}
          <SectionTitle icon={DollarSign}>Fee Summary</SectionTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "12px",
            }}
          >
            <FormField label="Instalment Amount">
              <Input
                type="number"
                value={installment.depositAmount}
                disabled={!customInstallments} // ← block editing if customInstallments = false
                onChange={(e) => {
                  if (customInstallments) {
                    onChange(idx, "depositAmount", e.target.value);
                  }
                }}
              />
              {!customInstallments && (
                <small style={{ color: "#6b7280" }}>
                  Amount auto-calculated (enable custom installments to edit)
                </small>
              )}
            </FormField>

            <FormField label="Due Date">
              <Input
                type="date"
                value={installment.dueDate || ""}
                onChange={(e) => onChange(idx, "dueDate", e.target.value)}
              />
            </FormField>
            <FormField label="Amount Paid">
              <Input
                type="number"
                value={installment.amountPaid || ""}
                onChange={(e) => onChange(idx, "amountPaid", e.target.value)}
              />
            </FormField>
            <FormField label="Status">
              <Select
                value={installment.status || ""}
                onChange={(e) => onChange(idx, "status", e.target.value)}
              >
                <option value="Pending">Pending</option>
                <option value="Paid">Paid</option>
                <option value="Overdue">Overdue</option>
              </Select>
            </FormField>
          </div>
          {/* Invoice Details Section */}
          <SectionTitle icon={Mail}>Invoice Details</SectionTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "12px",
              marginBottom: "16px"
            }}
          >
            <FormField label="Invoice Number">
              <Input
                type="text"
                value={installment.invoiceNumber || ""}
                disabled
                placeholder="Auto-generated on send"
              />
            </FormField>
            <FormField label="Invoice Sent Date">
              <Input
                type="date"
                value={installment.invoiceSentDate || ""}
                disabled
              />
            </FormField>
            <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", marginBottom: "16px" }}>
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleGenerateInvoice();
                }}
                loading={generatingInvoice}
                disabled={!installment.depositAmount || !installment.dueDate}
                style={{ flex: 1 }}
              >
                <Mail style={{ width: 16, height: 16, marginRight: 4 }} />
                {installment.invoiceSent ? "Resend Invoice" : "Send Invoice"}
              </Button>
              {installment.invoiceSent && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: invoiceSentColor,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  <CheckCircle
                    style={{
                      color: invoiceSentColor,
                      width: 24,
                      height: 24,
                    }}
                    title={invoiceSentTitle}
                  />
                  {isInvoiceSentWithFine && <span>Sent with fine</span>}
                </div>
              )}
            </div>
          </div>
          {/* Inside Expanded Body of InstallmentCard */}
          <SectionTitle icon={DollarSign}>Student Receipt / Payment Details</SectionTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "12px",
            }}
          >
            <FormField label="Invoice / Receipt Reference Date">
              <Input
                type="date"
                value={installment.receiptReferenceDate || ""}
                onChange={(e) => onChange(idx, "receiptReferenceDate", e.target.value)}
              />
            </FormField>

            <FormField label="Invoice / Receipt Reference No">
              <Input
                type="text"
                value={installment.receiptReferenceNo || ""}
                onChange={(e) => onChange(idx, "receiptReferenceNo", e.target.value)}
              />
            </FormField>
            <FormField label="Fees Paid Through">
              <Select
                value={installment.feesPaidTo || ""}
                onChange={(e) => onChange(idx, "feesPaidTo", e.target.value)}
              >
                <option value="">Select</option>
                <option value="Agent">Agent</option>
                <option value="College">Bank Transfer</option>
                <option value="EFTPOS">EFTPOS</option>
                <option value="Cash">Cash</option>
              </Select>
            </FormField>

            {installment.feesPaidTo === "Agent" && (
              <FormField label="Fees Received by College from Agent">
                <Select
                  value={installment.feesReceivedFromAgent || ""}
                  onChange={(e) => onChange(idx, "feesReceivedFromAgent", e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </Select>
              </FormField>
            )}
            <FormField label="Notes">
              <Textarea
                value={installment.notes || ""}
                onChange={(e) => onChange(idx, "notes", e.target.value)}
              />
            </FormField>
            <FormField label="Receipt Document" hint="Upload receipt documents">
              <FileInput accept=".pdf,.jpg,.jpeg,.png"
                maxSize={2 * 1024 * 1024} // 2 MB
                value={installment.receiptDoc} 
                onChange={file => onChange(idx, "receiptDoc", file)}
                placeholder="Upload a supporting document for the payment"
                 />
            </FormField>            
          </div>
          <SectionTitle icon={Receipt}>Payment Receipt Details</SectionTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "12px",
              marginBottom: "16px"
            }}
          >
            <FormField label="Receipt Number">
              <Input
                type="text"
                value={installment.receiptNumber || ""}
                disabled
                placeholder="Auto-generated on send"
              />
            </FormField>

            <FormField label="Receipt Sent Date">
              <Input
                type="date"
                value={installment.receiptSentDate || ""}
                disabled
              />
            </FormField>
            <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", marginBottom: "16px" }}>
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleGenerateReceipt();
                }}
                loading={generatingReceipt}
                disabled={!installment.amountPaid && !installment.depositAmount}
                style={{ flex: 1 }}
              >
                <Receipt style={{ width: 16, height: 16, marginRight: 4 }} />
                {installment.receiptSent ? "Resend Receipt" : "Send Receipt"}
              </Button>

              {installment.receiptSent && (
                <CheckCircle
                  style={{ color: "#10b981", width: 24, height: 24 }}
                  title="Receipt Sent"
                />
              )}
            </div>
          </div>


          {/* Remove Installment Button */}
          {onRemove && (
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <Button
                variant="destructive"
                onClick={(e) => {
                  e.stopPropagation(); // prevent collapsing
                  onRemove(idx);
                }}
              >
                Remove Installment
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
